import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Button, InlineStack, Banner } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { setDestroy, setIsModalxOpen } from "components/modalx/modalx.slice";
import { deleteDeskCard } from "services/desk.service";

interface DeleteDeskCardConfirmProps {
  companyId: string;
  /** Single-card delete (detail panel / legacy). */
  cardNumber?: number;
  /** Title of the single card, so the dialog names what it is about to delete. */
  cardTitle?: string;
  /** Bulk delete from the inbox/archive list. */
  cardNumbers?: number[];
  onConfirm?: () => void;
  isTrash?: boolean;
}

const DeleteDeskCardConfirm: React.FC<DeleteDeskCardConfirmProps> = ({
  companyId,
  cardNumber,
  cardTitle,
  cardNumbers,
  onConfirm,
  isTrash,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const initialNumbers =
    cardNumbers && cardNumbers.length
      ? cardNumbers
      : cardNumber != null
        ? [cardNumber]
        : [];
  // Remaining ids to delete — shrinks on partial success so retry skips gone cards.
  const [remainingNumbers, setRemainingNumbers] = useState<number[]>(initialNumbers);
  const count = remainingNumbers.length;

  const handleConfirm = async () => {
    if (!count) return;
    const toDelete = remainingNumbers;
    let nextIndex = 0;
    try {
      setIsLoading(true);
      setErrMsg(null);
      for (; nextIndex < toDelete.length; nextIndex++) {
        await deleteDeskCard(companyId, toDelete[nextIndex]);
      }
      closeModal();
      toast.success(t(isTrash ? "desk_deleted_permanently_done" : "desk_deleted_done", { count: toDelete.length }));
      if (onConfirm) onConfirm();
    } catch (e: any) {
      const left = toDelete.slice(nextIndex);
      setRemainingNumbers(left);
      // Refresh parent so successfully deleted cards leave the list.
      if (left.length < toDelete.length && onConfirm) onConfirm();
      setErrMsg(e?.response?.data?.message || e?.message || t("desk_delete_failed"));
    } finally {
      setIsLoading(false);
    }
  };

  const closeModal = () => {
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  };

  return (
    <div className="space-y-4">
      {errMsg && <Banner tone="critical">{errMsg}</Banner>}
      {/* Name the card so the user can check they are deleting the right one. */}
      {count === 1 && cardNumber != null && (
        <div className="text-center text-sm text-zinc-500 dark:text-zinc-400">
          #{cardNumber}
          {cardTitle ? ` — ${cardTitle}` : ""}
        </div>
      )}
      <div className="py-2 text-center text-sm font-semibold text-zinc-900 dark:text-zinc-100">
        {isTrash ? (
          count > 1
            ? t("desk_confirm_delete_cards_permanent", { count })
            : t("desk_confirm_delete_card_permanent")
        ) : (
          count > 1
            ? t("desk_confirm_delete_cards_soft", { count })
            : t("desk_confirm_delete_card_soft")
        )}
      </div>
      <InlineStack align="space-between">
        <Button onClick={handleConfirm} loading={isLoading} tone="critical" variant="primary">
          {t("desk_confirm_delete_card_yes")}
        </Button>
        <Button onClick={closeModal}>{t("desk_confirm_delete_card_no")}</Button>
      </InlineStack>
    </div>
  );
};

export default DeleteDeskCardConfirm;
